import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Eraser, Loader2, RefreshCw } from "lucide-react";
import {
  PURGE_FAMILIES,
  canResolvePage,
  cdnsForAssetPurge,
  cdnsForPath,
  describeCdns,
  getFamilyParams,
  purgePageLabel,
  resolvePurgePath,
  stockPageTypesForLinkKeys,
  type PurgeCdn,
  type PurgeFamilyDef,
  type PurgeResponse,
} from "../../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

/** Families grouped by their `group` heading, in registry order. */
const FAMILY_GROUPS = PURGE_FAMILIES.reduce<
  { group: string; families: PurgeFamilyDef[] }[]
>((acc, family) => {
  const bucket = acc.find((g) => g.group === family.group);
  if (bucket) bucket.families.push(family);
  else acc.push({ group: family.group, families: [family] });
  return acc;
}, []);

const CDN_LABELS: Record<PurgeCdn, string> = {
  "cloudflare-tags": "Cloudflare tags",
  "fastly-urls": "Fastly URLs",
  "cloudflare-urls": "Cloudflare URLs",
};

/** Small badges naming the CDN(s) a purge reaches. */
function CdnBadges({ cdns }: { cdns: readonly PurgeCdn[] }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {cdns.map((cdn) => (
        <Badge key={cdn} variant={cdn === "fastly-urls" ? "default" : "success"}>
          {CDN_LABELS[cdn]}
        </Badge>
      ))}
    </span>
  );
}

/** CDN(s) a page's purge reaches, mirroring the server's choice. */
function cdnsForPage(
  family: PurgeFamilyDef,
  path: string | null
): readonly PurgeCdn[] {
  if (family.host === "il") return ["cloudflare-urls"];
  switch (family.mechanism.kind) {
    case "stock-bff":
      return ["fastly-urls"];
    case "assets-rebuild":
      return cdnsForAssetPurge();
    case "url":
      return path ? cdnsForPath(path) : ["fastly-urls"];
  }
}

/** What the operator is actually about to trigger. */
function mechanismSummary(
  family: PurgeFamilyDef,
  urlCount: number,
  tagged: boolean
): string {
  switch (family.mechanism.kind) {
    case "stock-bff":
      return `Purge each tab's Worker cache tag and soft-purge its HTML on Fastly (${urlCount} URL(s))`;
    case "assets-rebuild":
      return `Purge the Cloudflare cache tag and re-warm the Worker now; also soft-purge all ${family.pageKeys.length} tabs on Fastly`;
    case "url":
      if (family.host === "il") {
        return `Purge ${urlCount} URL(s) from the Hebrew site (Cloudflare)`;
      }
      return tagged
        ? `Purge the Cloudflare cache tags of the Worker-served pages and re-warm them, and soft-purge ${urlCount} URL(s) on Fastly`
        : `Soft-purge ${urlCount} URL(s) from the website CDN (Fastly)`;
  }
}

export default function DataPurgePage() {
  const [familyKey, setFamilyKey] = useState<string>(PURGE_FAMILIES[0].key);
  const [params, setParams] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set(
        PURGE_FAMILIES[0].pageKeys.length === 1
          ? (PURGE_FAMILIES[0].pageKeys as readonly string[])
          : []
      )
  );
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<PurgeResponse | null>(null);

  const family = PURGE_FAMILIES.find((f) => f.key === familyKey)!;
  const paramSpecs = useMemo(() => getFamilyParams(family), [family]);

  // assets-rebuild acts on the whole asset (one Worker tag), so there is nothing to
  // select — the page list is shown to say what will be purged, not to filter.
  const effectiveKeys = family.wholeEntityOnly
    ? (family.pageKeys as readonly string[])
    : (family.pageKeys as readonly string[]).filter((k) => selected.has(k));

  const resolved = useMemo(
    () =>
      (family.pageKeys as readonly string[]).map((pageKey) => {
        const usable = canResolvePage(pageKey as never, params);
        return {
          pageKey,
          label: purgePageLabel(pageKey as never),
          usable,
          path: usable ? resolvePurgePath(pageKey as never, params) : null,
        };
      }),
    [family, params]
  );

  const selectedResolved = resolved.filter(
    (p) => effectiveKeys.includes(p.pageKey) && p.usable
  );

  /**
   * Exact URLs the request will touch. For `stock-bff` the unit of work is the
   * page *type*, not the selected URL — picking any one Financials sub-tab
   * purges all five of its HTML URLs — so the
   * preview has to expand accordingly or it under-reports the blast radius.
   */
  const previewPaths = useMemo<string[]>(() => {
    const ticker = params.ticker?.trim().toLowerCase();
    if (family.mechanism.kind === "stock-bff") {
      if (!ticker) return [];
      return stockPageTypesForLinkKeys(
        selectedResolved.map((p) => p.pageKey)
      ).flatMap((pageType) =>
        pageType.websitePaths.map((path) => `/stocks/${ticker}${path}`)
      );
    }
    return selectedResolved.flatMap((p) =>
      family.hasPayloadSibling
        ? [p.path!, `${p.path === "/" ? "" : p.path}/payload.json`]
        : [p.path!]
    );
  }, [family, params.ticker, selectedResolved]);

  const urlCount = previewPaths.length;
  const tagged = selectedResolved.some((p) =>
    cdnsForPage(family, p.path).includes("cloudflare-tags")
  );
  const missingParams = paramSpecs.filter((p) => !params[p.name]?.trim());
  const canSubmit =
    family.wholeEntityOnly
      ? !!params.ticker?.trim()
      : selectedResolved.length > 0;

  const reset = () => {
    setResult(null);
  };

  const switchFamily = (key: string) => {
    const next = PURGE_FAMILIES.find((f) => f.key === key)!;
    setFamilyKey(key);
    // A one-page family (an expert, a firm, a private company) has nothing to
    // choose — pre-select it so filling the params is the only step. Larger
    // families stay unselected; "select all" there should be deliberate.
    setSelected(
      next.pageKeys.length === 1
        ? new Set(next.pageKeys as readonly string[])
        : new Set()
    );
    setParams({});
    setResult(null);
  };

  const toggle = (pageKey: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pageKey)) next.delete(pageKey);
      else next.add(pageKey);
      return next;
    });
    reset();
  };

  const allSelected =
    selected.size === family.pageKeys.length && family.pageKeys.length > 0;

  const purgeMutation = useMutation({
    mutationFn: () =>
      api.data.purge({
        family: family.key,
        pageKeys: family.wholeEntityOnly || allSelected ? "all" : [...selected],
        params,
      }),
    onSuccess: (data) => {
      setResult(data);
      const failed = data.results.filter((r) => !r.ok).length;
      const tagsFailed = data.tags.filter((t) => !t.ok).length;
      if (failed + tagsFailed === 0 && data.warm?.ok !== false) {
        toast.success(
          `Purged ${data.results.length} URL(s)${data.tags.length > 0 ? ` and ${data.tags.length} Cloudflare tag(s)` : ""}.`
        );
      } else {
        toast.error(
          `${failed} URL purge(s), ${tagsFailed} tag purge(s)${data.warm?.ok === false ? " and the Worker warm" : ""} failed.`
        );
      }
      if (data.skipped.length > 0) {
        toast.warning(`${data.skipped.length} page(s) skipped — see below.`);
      }
    },
    onError: (e) => {
      toast.error(e instanceof ApiError ? e.message : "Something went wrong");
    },
  });

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Link
          to="/dashboard/data"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Data
        </Link>
        <h1 className="text-2xl font-bold">Cache Purge</h1>
        <p className="text-sm text-muted-foreground">
          Invalidate any page family on the site. Each family uses the mechanism
          that actually works for it — a stock tab is purged by Worker cache tag
          and on Fastly, an asset by its Worker cache tag plus all its tabs,
          and everything else is a plain URL purge.
        </p>
      </div>

      {/* ── Step 1: family ── */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="font-semibold">1. Page family</h2>
        <div className="mt-4 max-w-md space-y-2">
          <Label htmlFor="family">Family</Label>
          <select
            id="family"
            className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            value={familyKey}
            onChange={(e) => switchFamily(e.target.value)}
          >
            {FAMILY_GROUPS.map((g) => (
              <optgroup key={g.group} label={g.group}>
                {g.families.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label} ({f.pageKeys.length})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">{family.description}</p>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Badge
              variant={
                family.mechanism.kind === "assets-rebuild" ? "success" : "default"
              }
            >
              {family.mechanism.kind}
            </Badge>
            {family.host === "il" && <Badge variant="warning">Hebrew site</Badge>}
            {family.hasPayloadSibling && <Badge>+ payload.json</Badge>}
          </div>
        </div>
      </div>

      {/* ── Step 2: params ── */}
      {paramSpecs.length > 0 && (
        <div className="rounded-lg border bg-card p-6">
          <h2 className="font-semibold">2. Identify the page</h2>
          <div className="mt-4 grid max-w-2xl gap-4 sm:grid-cols-2">
            {paramSpecs.map((spec) => (
              <div key={spec.name} className="space-y-2">
                <Label htmlFor={`param-${spec.name}`}>{spec.label}</Label>
                {spec.allowed ? (
                  <select
                    id={`param-${spec.name}`}
                    className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                    value={params[spec.name] ?? ""}
                    onChange={(e) => {
                      setParams((p) => ({ ...p, [spec.name]: e.target.value }));
                      reset();
                    }}
                  >
                    <option value="">Pick a {spec.label.toLowerCase()}…</option>
                    {spec.allowed.map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id={`param-${spec.name}`}
                    value={params[spec.name] ?? ""}
                    placeholder={spec.placeholder}
                    onChange={(e) => {
                      setParams((p) => ({ ...p, [spec.name]: e.target.value }));
                      reset();
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          {family.mechanism.kind === "assets-rebuild" && (
            <p className="mt-3 text-xs text-muted-foreground">
              Comma-separate tickers to rebuild several at once.
            </p>
          )}
          {missingParams.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              Pages needing {missingParams.map((p) => p.label).join(" / ")} stay
              disabled until filled in.
            </p>
          )}
        </div>
      )}

      {/* ── Step 3: pages ── */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="font-semibold">
            {paramSpecs.length > 0 ? "3." : "2."} Pages
          </h2>
          {family.wholeEntityOnly ? (
            <span className="text-xs text-muted-foreground">
              All tabs — an asset is purged as a whole, not per tab
            </span>
          ) : family.mechanism.kind === "stock-bff" ? (
            <span className="text-xs text-muted-foreground">
              Selecting any Financials sub-tab covers the whole tab — they are
              purged together
            </span>
          ) : (
            <button
              type="button"
              onClick={() => {
                setSelected(
                  allSelected
                    ? new Set()
                    : new Set(family.pageKeys as readonly string[])
                );
                reset();
              }}
              className="text-sm text-primary hover:underline"
            >
              {allSelected ? "Clear selection" : "Select all"}
            </button>
          )}
        </div>
        <div className="grid gap-1 p-4 sm:grid-cols-2 lg:grid-cols-3">
          {resolved.map((page) => (
            <label
              key={page.pageKey}
              className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-sm ${
                family.wholeEntityOnly || !page.usable
                  ? "opacity-60"
                  : "cursor-pointer hover:bg-accent/50"
              }`}
            >
              <input
                type="checkbox"
                checked={
                  family.wholeEntityOnly ? true : selected.has(page.pageKey)
                }
                disabled={family.wholeEntityOnly || !page.usable}
                onChange={() => toggle(page.pageKey)}
                className="mt-0.5 accent-primary"
              />
              <span className="min-w-0">
                <span className="font-medium">{page.label}</span>
                <span className="block break-all font-mono text-xs text-muted-foreground">
                  {page.path ?? "needs more info above"}
                </span>
                {page.path && (
                  <span className="mt-0.5 block">
                    <CdnBadges cdns={cdnsForPage(family, page.path)} />
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Button
          onClick={() => setConfirming(true)}
          disabled={!canSubmit || purgeMutation.isPending}
        >
          {purgeMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : family.mechanism.kind === "assets-rebuild" ? (
            <RefreshCw className="h-4 w-4" />
          ) : (
            <Eraser className="h-4 w-4" />
          )}
          {family.mechanism.kind === "assets-rebuild"
            ? "Rebuild & purge"
            : "Purge"}
        </Button>
        {canSubmit && (
          <p className="text-xs text-muted-foreground">
            {mechanismSummary(family, urlCount, tagged)}
          </p>
        )}
        {/* Each stock tab is a sequential purge + warm, so a big selection
            can outrun the edge's request timeout. The Stock ETL page's full
            update does the whole stock in the background instead. */}
        {family.mechanism.kind === "stock-bff" && urlCount > 20 && (
          <p className="text-xs text-warning">
            That's a lot of sequential tab purges and may time out. For a
            whole stock, use{" "}
            <Link
              to="/dashboard/data/stock-etl"
              className="underline hover:no-underline"
            >
              Stock ETL → Full update
            </Link>
            , which runs in the background.
          </p>
        )}
      </div>

      {result && (
        <div className="space-y-4 rounded-lg border bg-card">
          <div className="border-b px-6 py-4">
            <h2 className="font-semibold">Result</h2>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              CDN: {describeCdns(result.cdns)} <CdnBadges cdns={result.cdns} />
            </p>
          </div>

          {result.upstream && (
            <div className="px-6 text-sm">
              <p>
                <span className="font-medium">Upstream rebuild:</span>{" "}
                <span
                  className={
                    result.upstream.status === "failed"
                      ? "text-destructive"
                      : "text-emerald-600 dark:text-emerald-400"
                  }
                >
                  {result.upstream.status}
                </span>
                {result.upstream.message && (
                  <span className="text-muted-foreground">
                    {" "}
                    — {result.upstream.message}
                  </span>
                )}
              </p>
              <p className="mt-1 break-all font-mono text-xs text-muted-foreground">
                {result.upstream.url}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                The upstream function purges the payload and every HTML tab on
                Fastly itself, so there are no per-URL results here.
              </p>
            </div>
          )}

          {result.schedule.length > 0 && (
            <div className="px-6 text-sm">
              <p className="font-medium">Cloudflare tag purge schedule</p>
              <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                {result.schedule.map((step) => (
                  <li key={step.afterSeconds}>
                    +{step.afterSeconds} s:{" "}
                    <span className="font-mono">
                      {new Date(step.at).toLocaleTimeString()}
                    </span>{" "}
                    purge the tags, then re-warm the Worker
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">
                The rebuild queue gives no completion signal, so the purge runs
                twice. The timers live in the API process: if it restarts first,
                run the purge again. Each run is written to the audit log.
              </p>
            </div>
          )}

          {result.tags.length > 0 && (
            <div className="px-6 text-sm">
              <p className="font-medium">Cloudflare cache tags</p>
              <ul className="mt-1 space-y-1 text-xs">
                {result.tags.map((t) => (
                  <li key={t.tag} className="flex flex-wrap gap-2">
                    <span className="break-all font-mono">{t.tag}</span>
                    <span
                      className={
                        t.ok
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-destructive"
                      }
                    >
                      {t.ok ? "OK" : `HTTP ${t.status}`}
                    </span>
                    <span className="font-mono text-muted-foreground">
                      {t.purgeId ?? "—"}
                    </span>
                  </li>
                ))}
              </ul>
              {result.warm ? (
                <p className="mt-2 text-xs">
                  <span className="font-medium">Worker warm (KV):</span>{" "}
                  <span
                    className={
                      result.warm.ok
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-destructive"
                    }
                  >
                    {result.warm.ok ? "OK" : "failed"}
                  </span>
                  {result.warm.message && (
                    <span className="text-muted-foreground">
                      {" "}
                      — {result.warm.message}
                    </span>
                  )}
                </p>
              ) : (
                <p className="mt-2 text-xs text-destructive">
                  Worker warm skipped: a tag purge failed.
                </p>
              )}
            </div>
          )}

          {result.skipped.length > 0 && (
            <div className="px-6">
              <p className="text-sm font-medium">Skipped</p>
              <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                {result.skipped.map((s) => (
                  <li key={s.pageKey}>
                    <span className="font-mono">{s.pageKey}</span> — {s.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.results.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>URL</TableHead>
                  <TableHead className="w-48">CDN</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-56">Purge ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.results.map((r) => (
                  <TableRow key={r.url}>
                    <TableCell className="break-all font-mono text-xs">
                      {r.url}
                    </TableCell>
                    <TableCell>
                      {r.cdns && <CdnBadges cdns={r.cdns} />}
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          r.ok
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-destructive"
                        }
                      >
                        {r.ok ? "OK" : `HTTP ${r.status}`}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {r.purgeId ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="pb-2" />
        </div>
      )}

      {/* Confirm dialog */}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {family.mechanism.kind === "assets-rebuild"
                ? `Rebuild ${family.label} ${params.ticker?.toUpperCase() ?? ""}?`
                : `Purge ${urlCount} URL(s)?`}
            </DialogTitle>
            <DialogDescription>
              {mechanismSummary(family, urlCount, tagged)}.{" "}
              {family.host === "il"
                ? "Cloudflare purges immediately."
                : "Fastly soft purge marks objects stale: the next request revalidates against origin. A Cloudflare tag purge evicts the Worker copy at once."}
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-60 space-y-1 overflow-auto rounded-lg border bg-muted/30 p-3 font-mono text-xs">
            {previewPaths.map((path) => (
              <li key={path} className="break-all">
                {path}
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirming(false);
                purgeMutation.mutate();
              }}
            >
              {family.mechanism.kind === "assets-rebuild"
                ? "Rebuild & purge"
                : "Purge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
